<template>
  <div class="bg-marketing-light pb-5">
    <!-- Breadcrumb Section -->
    <section class="bg-white py-3" style="border-bottom: 1px solid #E2E8F0;">
      <CContainer>
        <div class="d-flex align-items-center small text-muted">
          <router-link to="/" class="text-marketing-gray-600 font-weight-500" style="text-decoration: none;">Home</router-link>
          <CIcon name="cil-chevron-right" size="sm" class="mx-2"/>
          <router-link to="/calculators" class="text-marketing-gray-600 font-weight-500" style="text-decoration: none;">Calculators</router-link>
          <CIcon name="cil-chevron-right" size="sm" class="mx-2"/>
          <span class="text-marketing-dark font-weight-bold">Take-Home Salary Calculator</span>
        </div>
      </CContainer>
    </section>

    <!-- Hero Section -->
    <section class="py-5 bg-white">
      <CContainer>
        <CRow class="justify-content-center">
          <CCol lg="8" class="text-center">
            <div class="marketing-icon-wrapper mx-auto mb-4" style="width: 80px; height: 80px; font-size: 2rem;">
              <CIcon name="cil-money" size="custom-size" style="width: 40px; height: 40px;" class="text-info"/>
            </div>
            <h1 class="display-4 font-weight-bold marketing-heading mb-3">
              Take-Home Salary Calculator
            </h1>
            <p class="lead marketing-body mb-0">
              Calculate your net salary after EPF, SOCSO, and EIS deductions instantly.
            </p>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Main Calculator Section -->
    <section class="py-5">
      <CContainer>
        <CRow>
          <!-- Left Column: Interactive Calculator -->
          <CCol lg="8" class="mb-4">
            <CCard class="border-0 shadow-sm h-100">
              <CCardBody class="p-4 p-md-5">
                <h2 class="h3 font-weight-bold text-marketing-dark mb-4">Calculate Net Salary</h2>

                <!-- Input: Salary -->
                <div class="form-group mb-4">
                  <label class="font-weight-600 text-marketing-dark">Monthly Gross Salary (RM)</label>
                  <div class="input-group input-group-lg">
                    <div class="input-group-prepend">
                      <span class="input-group-text bg-white border-right-0 text-muted font-weight-bold">RM</span>
                    </div>
                    <input 
                      type="number" 
                      class="form-control border-left-0 pl-0" 
                      v-model.number="salary"
                      @input="calculate"
                      placeholder="5000"
                      min="0"
                      step="100"
                      style="box-shadow: none;"
                    >
                  </div>
                </div>

                <!-- Input: Age Range Buttons -->
                <div class="form-group mb-4">
                  <label class="font-weight-600 text-marketing-dark mb-3">Employee Age Profile</label>
                  <CRow>
                    <CCol md="6" class="mb-2">
                       <CButton 
                         block
                         :color="ageRange === 'below60' ? 'info' : 'light'"
                         class="py-3 font-weight-bold h-100"
                         style="border-width: 2px;"
                         :class="{'border-info': ageRange === 'below60', 'text-white': ageRange === 'below60'}"
                         @click="selectAge('below60')"
                       >
                         Below 60 Years Old
                       </CButton>
                    </CCol>
                    <CCol md="6" class="mb-2">
                       <CButton 
                         block
                         :color="ageRange === 'above60' ? 'info' : 'light'"
                         class="py-3 font-weight-bold h-100"
                         style="border-width: 2px;"
                         :class="{'border-info': ageRange === 'above60', 'text-white': ageRange === 'above60'}"
                         @click="selectAge('above60')"
                       >
                         60 - 75 Years Old
                       </CButton>
                    </CCol>
                  </CRow>
                </div>

                <!-- Results Panel (Active) -->
                <div v-if="salary > 0" class="bg-info text-white p-4 p-md-5 rounded-lg mt-5" style="background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);">
                  
                  <div class="text-center mb-5 position-relative z-index-1">
                    <p class="small font-weight-bold text-uppercase letter-spacing-1 mb-2 opacity-80">Your Net Take-Home Salary</p>
                    <div class="display-3 font-weight-bold mb-0">RM {{ netSalary.toFixed(2) }}</div>
                  </div>

                  <h3 class="h6 font-weight-bold mb-3 border-bottom border-light pb-2" style="border-color: rgba(255,255,255,0.2) !important;">
                    Monthly Deductions Breakdown
                  </h3>

                  <div class="mb-4">
                     <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="opacity-80 small">Gross Salary</span>
                        <span class="font-weight-bold">RM {{ salary.toFixed(2) }}</span>
                     </div>
                     <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="opacity-80 small">EPF ({{ ageRange === 'below60' ? '11%' : '4%' }})</span>
                        <span class="font-weight-bold" style="color: #fca5a5;">- RM {{ epfAmount.toFixed(2) }}</span>
                     </div>
                     <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="opacity-80 small">SOCSO (Estimated)</span>
                        <span class="font-weight-bold" style="color: #fca5a5;">- RM {{ socsoAmount.toFixed(2) }}</span>
                     </div>
                     <div v-if="eisAmount > 0" class="d-flex justify-content-between align-items-center mb-2">
                        <span class="opacity-80 small">EIS (~0.2% max RM6k)</span>
                        <span class="font-weight-bold" style="color: #fca5a5;">- RM {{ eisAmount.toFixed(2) }}</span>
                     </div>
                  </div>

                  <div class="pt-3 border-top" style="border-color: rgba(255,255,255,0.2) !important;">
                     <div class="d-flex justify-content-between align-items-center">
                        <span class="opacity-80">Total Recommended Employer Cost:</span>
                        <strong class="font-weight-bold h5 mb-0">RM {{ employerCost.toFixed(2) }}</strong>
                     </div>
                  </div>

                </div>

                <!-- Empty State -->
                <div v-else class="mt-5 p-5 border text-center rounded-lg" style="border-style: dashed !important; border-width: 2px !important; border-color: #cbd5e1 !important; background: #f8fafc;">
                   <CIcon name="cil-money" size="2xl" class="text-muted mb-3 opacity-50"/>
                   <p class="text-muted font-weight-600 mb-0">Enter your gross salary above to preview your net take-home pay.</p>
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Right Column: Info Sidebar -->
          <CCol lg="4">
            <!-- Quick Facts Card -->
            <CCard class="border-0 shadow-sm mb-4">
              <CCardBody class="p-4">
                 <h3 class="h5 font-weight-bold text-marketing-dark mb-4">Deduction Details</h3>

                 <div class="mb-4">
                   <h6 class="font-weight-bold text-marketing-dark mb-1">PCB (Income Tax)</h6>
                   <p class="text-muted small mb-0">This basic calculator does not include PCB (Potongan Cukai Berjadual) estimation because income tax depends on complex individual factors like marital status, children, and relief claims.</p>
                 </div>
                 
                 <div class="mb-4 border-top pt-3">
                   <h6 class="font-weight-bold text-marketing-dark mb-1">SOCSO / EIS Ceiling</h6>
                   <p class="text-muted small mb-0">Note that SOCSO & EIS cap deductions at a maximum salary ceiling base of <strong>RM 6,000</strong>.</p>
                 </div>

                 <div class="border-top pt-4 text-center mt-5">
                   <CButton block class="btn-info py-3 font-weight-bold mb-2 text-white" to="/signup">
                     Get PCB Included Payslips
                   </CButton>
                   <small class="text-muted">Sign up for comprehensive payroll.</small>
                 </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'SalaryCalculator',
  metaInfo: {
    title: 'Net Salary Calculator Malaysia - Take Home Pay Simulator',
    meta: [
      {
         name: 'description',
         content: 'Calculate your net take-home salary in Malaysia after EPF, SOCSO, and EIS deductions online. Free calculator.'
      }
    ]
  },
  data() {
    return {
      salary: 5000,
      ageRange: 'below60',
      epfAmount: 0,
      socsoAmount: 0,
      eisAmount: 0,
      netSalary: 0,
      employerCost: 0
    }
  },
  mounted() {
    this.calculate()
  },
  methods: {
    selectAge(range) {
      this.ageRange = range;
      this.calculate()
    },
    calculate() {
      if (!this.salary || this.salary <= 0) {
        this.epfAmount = 0;
        this.socsoAmount = 0;
        this.eisAmount = 0;
        this.netSalary = 0;
        this.employerCost = 0;
        return;
      }

      let gross = parseFloat(this.salary);
      let socsoCapped = Math.min(gross, 6000);

      // EPF Setup
      let epfRate = this.ageRange === 'below60' ? 0.11 : 0.04;
      let erEpfRate = 0;
      if (this.ageRange === 'below60') {
         erEpfRate = gross <= 5000 ? 0.13 : 0.12;
      } else {
         erEpfRate = 0.04;
      }
      this.epfAmount = gross * epfRate;
      let erEpfAmount = gross * erEpfRate;

      // SOCSO/EIS Setup
      if (this.ageRange === 'below60') {
         // Table 1
         this.socsoAmount = Math.round(((socsoCapped * 0.005) + Number.EPSILON) * 4) / 4;
         let erSocsoAmount = Math.round(((socsoCapped * 0.0175) + Number.EPSILON) * 20) / 20;

         // EIS
         this.eisAmount = Math.ceil(((socsoCapped * 0.002) + Number.EPSILON) * 10) / 10;
         let erEisAmount = Math.ceil(((socsoCapped * 0.002) + Number.EPSILON) * 10) / 10;
         
         this.employerCost = gross + erEpfAmount + erSocsoAmount + erEisAmount;
      } else {
         // Above 60. No employee socso or EIS. Employer pays Table 2.
         this.socsoAmount = 0;
         this.eisAmount = 0;
         let erSocsoAmount = Math.round(((socsoCapped * 0.0125) + Number.EPSILON) * 20) / 20;

         this.employerCost = gross + erEpfAmount + erSocsoAmount;
      }

      this.netSalary = gross - this.epfAmount - this.socsoAmount - this.eisAmount;
    }
  }
}
</script>

<style scoped>
.hover-bg-light:hover {
  background-color: #f8fafc;
}
.transition-all {
  transition: all 0.2s ease-in-out;
}
.letter-spacing-1 {
  letter-spacing: 1px;
}
.opacity-80 {
  opacity: 0.8;
}
.opacity-75 {
  opacity: 0.75;
}
.opacity-50 {
  opacity: 0.5;
}
.z-index-1 {
  z-index: 1;
}
</style>
